/**
 * DocContent - Pure article renderer for documentation reader
 *
 * Features:
 * - Document header with editable title and badges
 * - Always-on editing with BlockNoteEditor (no read/edit toggle)
 * - Auto-save on change with debounce (1500ms)
 *
 * Chrome (header bar, footer nav, loading/empty states) handled by parent
 * via ReaderCenterShell props.
 */

import { useState, useRef, useCallback, useEffect, useMemo, lazy, Suspense } from 'react';
import {
  ChevronRight,
  SlidersHorizontal,
  Volume2,
  Pause,
  Loader2,
  Square,
  Copy,
} from 'lucide-react';
import { resolveInitialContent, type EditorDocument } from '../../api/documents';
import PropertyValuesTable from './PropertyValuesTable';
import { useNarration } from '../../hooks/useNarration';

// Lazy load MarthaEditor (unified editor)
const MarthaEditor = lazy(
  () => import('../editor/MarthaEditor')
);

interface DocContentProps {
  document: EditorDocument;
  projectId?: string;
  isSaving?: boolean;
  onSave?: (content: string, contentFormat?: string) => void;
  onTitleChange?: (title: string) => void;
  onSelectionChange?: (text: string | null, start: number, end: number) => void;
  onCommentClick?: () => void;
  // Collaboration (Phase 4)
  collaborationConfig?: import('../editor/MarthaEditor').CollaborationConfig;
  isCollaborationConnected?: boolean;
  /** True once Yjs sync step 2 completes — used to trigger content fallback immediately */
  isCollaborationSynced?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  collaborationUsers?: Array<{ id: string; name: string; color?: string }>;
}

// Format date helper
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// Get document type badge
function getDocTypeBadge(doc: EditorDocument) {
  if (doc.tags?.includes('requirements')) {
    return { label: 'Requirements', className: 'bg-coral-50 text-coral-700 border-coral-200' };
  }
  if (doc.doc_type === 'spec') {
    return { label: 'Specification', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' };
  }
  if (doc.doc_type === 'test') {
    return { label: 'Test Plan', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  }
  return { label: 'Document', className: 'bg-neutral-100 text-neutral-600 border-neutral-200' };
}

// Loading fallback for editor — shimmer skeleton instead of spinner to avoid flash
function EditorLoadingFallback() {
  return (
    <div className="animate-pulse space-y-4 py-4">
      <div className="h-4 bg-neutral-100 rounded w-full" />
      <div className="h-4 bg-neutral-100 rounded w-11/12" />
      <div className="h-4 bg-neutral-100 rounded w-4/5" />
      <div className="h-4 bg-neutral-50 rounded w-full" />
      <div className="h-4 bg-neutral-50 rounded w-3/4" />
      <div className="space-y-3 mt-6">
        <div className="h-4 bg-neutral-100 rounded w-full" />
        <div className="h-4 bg-neutral-100 rounded w-5/6" />
        <div className="h-4 bg-neutral-50 rounded w-full" />
        <div className="h-4 bg-neutral-50 rounded w-2/3" />
      </div>
    </div>
  );
}

export function DocContent({
  document,
  projectId,
  isSaving = false,
  onSave,
  onTitleChange,
  onSelectionChange,
  onCommentClick,
  collaborationConfig,
  isCollaborationConnected,
  isCollaborationSynced,
  collaborationUsers,
}: DocContentProps) {
  const editorRef = useRef<{ getMarkdown: () => Promise<string> }>(null);
  const [editedTitle, setEditedTitle] = useState('');
  // lastSavedContent lives in a ref, not state: handleEditorChange reads it
  // on every invocation to decide whether to save, but nothing renders from
  // it. Using state here would re-create handleEditorChange on every save →
  // rebuild MarthaEditor's debounced onChange → break debouncing and (pre
  // PR #471) feed instability into downstream useEffect deps.
  const lastSavedContentRef = useRef('');
  // Mirror onSave in a ref too so handleEditorChange stays stable even when
  // the parent rebuilds its callback on every render (react-query mutation
  // objects are unstable by design).
  const onSaveRef = useRef(onSave);
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stabilize the features object so MarthaEditor doesn't see a new prop
  // reference on every render. Only the two conditional flags actually vary.
  const editorFeatures = useMemo(
    () => ({
      outline: true,
      mermaid: true,
      stats: true,
      breadcrumb: true,
      comments: !!projectId,
      collaboration: !!collaborationConfig,
    }),
    [projectId, collaborationConfig],
  );

  // Collapsible properties
  const [propsCollapsed, setPropsCollapsed] = useState(() => {
    return localStorage.getItem('martha.doc.properties.collapsed') === 'true';
  });
  const [propsCount, setPropsCount] = useState(0);
  const toggleProps = useCallback(() => {
    setPropsCollapsed(prev => {
      localStorage.setItem('martha.doc.properties.collapsed', String(!prev));
      return !prev;
    });
  }, []);

  // Initialize title when document changes
  useEffect(() => {
    if (document?.title) {
      setEditedTitle(document.title);
    }
  }, [document?.title]);

  // Seed the saved-content ref on document change.
  useEffect(() => {
    if (document) {
      lastSavedContentRef.current = resolveInitialContent(document);
    }
  }, [document?.id]);

  // Reset property count on document change so hidden fetch triggers
  useEffect(() => {
    setPropsCount(0);
  }, [document?.id]);

  // Debounced auto-save for content (1500ms). Reads the onSave callback and
  // the last-saved snapshot from refs so handleEditorChange has a stable
  // identity across renders — without that, MarthaEditor's debounced onChange
  // would rebuild on every keystroke and effectively stop debouncing.
  const handleEditorChange = useCallback(
    (content: string, contentFormat?: string) => {
      const onSaveNow = onSaveRef.current;
      if (!onSaveNow) return;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      if (content === lastSavedContentRef.current) return;

      saveTimeoutRef.current = setTimeout(() => {
        onSaveNow(content, contentFormat);
        lastSavedContentRef.current = content;
      }, 1500);
    },
    [],
  );

  // Debounced title change (1000ms)
  const handleTitleChange = useCallback(
    (newTitle: string) => {
      setEditedTitle(newTitle);

      if (!onTitleChange) return;

      // Clear existing timeout
      if (titleTimeoutRef.current) {
        clearTimeout(titleTimeoutRef.current);
      }

      // Set new debounced save
      titleTimeoutRef.current = setTimeout(() => {
        onTitleChange(newTitle);
      }, 1000);
    },
    [onTitleChange]
  );

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (titleTimeoutRef.current) clearTimeout(titleTimeoutRef.current);
    };
  }, []);

  const badge = getDocTypeBadge(document);
  const authorInitial = document.created_by?.charAt(0)?.toUpperCase() || '?';
  const narration = useNarration();

  const handleListen = useCallback(() => {
    if (narration.isPlaying || narration.isLoading) {
      narration.togglePlayback();
    } else if (projectId && document.id) {
      narration.startNarration(document.id, projectId);
    }
  }, [narration, projectId, document.id]);

  return (
    <div className="flex-1 overflow-y-auto">
      <article className="px-8 py-8 lg:px-12 lg:py-10 max-w-4xl">
        {/* Badge + ID + Listen button */}
        <div className="flex items-center gap-3 mb-4">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border ${badge.className}`}
          >
            {badge.label}
          </span>

          {/* Document ID — click to copy */}
          <button
            onClick={() => navigator.clipboard.writeText(document.id).catch(() => {})}
            className="inline-flex items-center gap-1 font-mono text-[11px] text-neutral-400 hover:text-neutral-600 bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 px-2 py-0.5 rounded transition-colors"
            title="Click to copy document ID"
          >
            <Copy size={10} />
            {document.id}
          </button>

          {/* Listen / Narration button */}
          {projectId && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleListen}
                disabled={narration.isLoading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium
                         text-coral-600 bg-coral-50 hover:bg-coral-100 rounded-full border border-coral-200/60
                         transition-all duration-200 disabled:opacity-50"
                title={narration.isPlaying ? 'Pause narration' : 'Listen to this document'}
              >
                {narration.isLoading ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : narration.isPlaying ? (
                  <Pause size={12} />
                ) : (
                  <Volume2 size={12} />
                )}
                {narration.isLoading ? 'Loading...' : narration.isPlaying ? 'Pause' : 'Listen'}
              </button>

              {(narration.isPlaying || narration.progress > 0) && (
                <>
                  {/* Progress bar */}
                  <div className="w-20 h-1 bg-coral-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-coral-400 rounded-full transition-[width] duration-300"
                      style={{ width: `${narration.progress * 100}%` }}
                    />
                  </div>

                  {/* Stop button */}
                  <button
                    onClick={narration.stopNarration}
                    className="p-1 text-coral-400 hover:text-coral-600 transition-colors"
                    title="Stop narration"
                  >
                    <Square size={10} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Editable Title */}
        <input
          type="text"
          value={editedTitle}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Untitled document"
          className="w-full text-3xl lg:text-4xl font-bold text-neutral-900 tracking-tight leading-tight mb-3
                   bg-transparent border-0 outline-none focus:ring-0 placeholder:text-neutral-300
                   hover:bg-neutral-50 focus:bg-neutral-50 rounded-lg px-2 -mx-2 py-1 transition-colors"
          style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
        />

        {/* Meta line — coral avatar pattern matching SM */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500 mb-8 pb-6 border-b border-neutral-100">
          {document.created_by && (
            <span className="flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-coral-100 flex items-center justify-center text-[10px] font-bold text-coral-600">
                {authorInitial}
              </span>
              {document.created_by}
            </span>
          )}
          <span className="text-xs text-neutral-400">
            Updated {formatDate(document.updated_at)}
          </span>
          {document.tags && document.tags.length > 0 && (
            <span className="text-xs text-neutral-400">
              {document.tags.filter((t) => t !== 'requirements').slice(0, 3).join(', ')}
            </span>
          )}
        </div>

        {/* Collapsible Property Values */}
        {projectId && document.id && (
          <div className={propsCount > 0 ? 'mb-4' : ''}>
            {propsCount > 0 && (
              <button
                onClick={toggleProps}
                className="flex items-center gap-2 w-full px-3 py-2 text-xs font-medium text-neutral-500
                         hover:text-neutral-700 hover:bg-neutral-50 rounded-lg transition-colors"
              >
                <ChevronRight size={14} className={`transition-transform ${propsCollapsed ? '' : 'rotate-90'}`} />
                <SlidersHorizontal size={14} />
                <span>Properties ({propsCount})</span>
              </button>
            )}
            {(!propsCollapsed || propsCount === 0) && (
              <div className={propsCollapsed ? 'hidden' : undefined}>
                <PropertyValuesTable documentId={document.id} projectId={projectId} onPropertiesLoaded={setPropsCount} />
              </div>
            )}
          </div>
        )}

        {/* Always-on Editor */}
        <Suspense fallback={<EditorLoadingFallback />}>
          <div className="min-h-[500px] -mx-4">
            <MarthaEditor
              key={`${document.id}-${collaborationConfig ? 'c' : 's'}`}
              ref={editorRef}
              initialContent={resolveInitialContent(document)}
              contentFormat={document.content_format}
              onChange={handleEditorChange}
              onSelectionChange={onSelectionChange}
              onCommentClick={onCommentClick}
              autoSaveDelay={1500}
              documentId={document.id}
              projectId={projectId}
              features={editorFeatures}
              collaborationConfig={collaborationConfig}
              isCollaborationConnected={isCollaborationConnected}
              isCollaborationSynced={isCollaborationSynced}
              collaborationUsers={collaborationUsers}
            />
          </div>
        </Suspense>
      </article>

    </div>
  );
}

export default DocContent;
